import React from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { Workspace } from "@common/models";
import {
  Azure,
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";
import { AzureAuthorizeModal } from "main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/AzureAuthorizationModal";

import { Form } from "../ModalBody.styles";
import { AzureOption, useAzureIntegrationFormConsumer } from "./consumers/useAzureIntegrationFormConsumer";

interface AzureIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Azure;
  selectedAppId: string;
  workspace: Workspace;
  onAuthSuccess: () => void;
  onFinish: () => void;
}

export const AzureIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
  workspace,
  onAuthSuccess,
}: AzureIntegrationModalProps) => {
  const {
    forwardingOption,
    projectOption,
    projectOptions,
    handleFormChange,
    handleFormSubmit,
    disabled,
    loading,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useAzureIntegrationFormConsumer({ selectedAppId, onFinish, integration });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, projectOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  if (!integration) {
    return (
      <AzureAuthorizeModal
        Modal={Modal}
        modalProps={modalProps}
        workspaceId={workspace.id}
        selectedAppId={selectedAppId}
        onFinish={onAuthSuccess}
      />
    );
  }

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.AZURE_DEVOPS}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.AZURE_DEVOPS)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.AZURE_DEVOPS)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="projectOption"
          value={projectOption}
          options={projectOptions}
          placeholder="Please choose"
          label="Project"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />
        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  projectOption: Option,
  forwardingOption: Option,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Azure,
) => {
  if (disabled) return true;
  if (!projectOption) return true;
  if (projectOption.value === "to_azure") return false;
  if (!integration) return false;

  const parsedProjectOption = JSON.parse(projectOption.value) as AzureOption;

  if (
    parsedProjectOption.projectName &&
    (parsedProjectOption.projectName !== integration?.project_name ||
      !!forwardingOption.value !== integration?.automatic_forward)
  ) {
    return false;
  }

  // Check if integration layout has changed
  return !hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout);
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
